import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { LogOut, User, ChevronRight, UserPlus, Crown, ArrowLeft, Mail, Lock, Plus, MessageSquare, Copy, Check, Trophy, HelpCircle, X } from 'lucide-react';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { cn } from '../lib/utils';
import { clearPushRegistration } from '../lib/push-notifications';
import { SupportForm } from './SupportForm';
import { HelpCenter } from './HelpCenter';
import { TermsOfService, PrivacyPolicy } from './LegalModals';
import { ManageAccountModal } from './ManageAccountModal';
import { formatGrantExpiry } from '../lib/entitlement';
import { Capacitor } from '@capacitor/core';
import { openManageSubscriptions } from '../lib/iap';

interface Props {
  user: any;
  coachData: any;
  team?: any;
  teams?: any[];
  selectedTeamId?: string | null;
  isHeadCoach?: boolean;
  isElite?: boolean;
  eliteSources?: { stripe: boolean; iap: boolean; grant: boolean };
  /** Season Pass end date (epoch ms), or null for a founding comp / no grant. */
  grantExpiresAt?: number | null;
  onSelectTeam?: (id: string) => void;
  onAddTeam?: () => void;
  onNavigate: (tab: string) => void;
  onUpgrade?: () => void;
}

export const UserAccountMenu: React.FC<Props> = ({ user, coachData, team, teams = [], selectedTeamId, isHeadCoach = true, isElite = false, eliteSources, grantExpiresAt = null, onSelectTeam, onAddTeam, onNavigate, onUpgrade }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<'menu' | 'invite'>('menu');
  const [isShowingSupportForm, setIsShowingSupportForm] = useState(false);
  const [isShowingHelp, setIsShowingHelp] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [isShowingManageAccount, setIsShowingManageAccount] = useState(false);

  // null for a founding comp (no expiry) and for Stripe/IAP subscribers, so only
  // an actual Season Pass ever renders an end date.
  const seasonPassEnds = formatGrantExpiry(grantExpiresAt);

  // Prefer the Firestore users-doc photo (coachData) over Firebase Auth's
  // photoURL. Uploaded photos are base64 data URLs stored only in Firestore
  // (Auth rejects them as too long); Google sign-in mirrors its https photo to
  // both, so this covers every case.
  const avatarPhoto = coachData?.photoURL || user?.photoURL || '';

  // "Dugout Coach Since ####" — derived from the account's creation date.
  // Firebase Auth stamps user.metadata.creationTime; fall back to coachData.createdAt.
  const coachSinceYear = (() => {
    const raw = user?.metadata?.creationTime || coachData?.createdAt;
    const d = raw ? new Date(raw) : null;
    return d && !isNaN(d.getTime()) ? d.getFullYear() : new Date().getFullYear();
  })();
  const [copiedCode, setCopiedCode] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const handleManageSubscription = async () => {
    setIsOpen(false);
    // Native means an Apple IAP subscription, managed in iOS Settings rather than
    // the Stripe portal. A web-Stripe subscriber on iOS never reaches this handler:
    // ManageAccountModal only renders the button when the destination is actually
    // reachable from this platform, and shows a sentence otherwise. Without that
    // check this used to open an EMPTY Apple subscriptions screen and read as
    // "I can't cancel".
    if (Capacitor.isNativePlatform()) {
      await openManageSubscriptions();
      return;
    }
    setPortalLoading(true);
    try {
      const functions = getFunctions();
      const createPortalLink = httpsCallable(functions, 'ext-firestore-stripe-payments-createPortalLink');
      const { data } = await createPortalLink({ returnUrl: window.location.origin }) as any;
      window.location.assign(data.url);
    } catch (e) {
      console.error('Portal link failed', e);
    } finally {
      setPortalLoading(false);
    }
  };
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setView('menu');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    // Best-effort: drop this device's push token from the account first, so a
    // shared device doesn't keep getting the signed-out coach's Scout nudges.
    void clearPushRegistration();
    signOut(auth);
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Profile Trigger */}
      <div className="relative">
        <button
          onClick={() => { setIsOpen(!isOpen); setView('menu'); }}
          className={cn(
            "w-10 h-10 rounded-full border-2 transition-all overflow-hidden flex items-center justify-center bg-canvas shadow-lg",
            isOpen ? "border-ink scale-105" : "border-hairline hover:border-ink-mute"
          )}
        >
          {avatarPhoto ? (
            <img
              src={avatarPhoto}
              referrerPolicy="no-referrer"
              alt="User"
              className="w-full h-full object-cover"
            />
          ) : (
            <User className="w-5 h-5 text-ink-2" />
          )}
        </button>
        {isElite && (
          <span className="absolute -bottom-3.5 left-1/2 -translate-x-1/2 text-[8px] font-semibold uppercase tracking-widest text-accent-ink whitespace-nowrap pointer-events-none">
            Elite
          </span>
        )}
      </div>

      {/* Dropdown Menu */}
      <AnimatePresence mode="wait">
        {isOpen && (
          <motion.div
            key="dropdown"
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute top-full right-0 mt-3 w-72 bg-canvas border border-hairline rounded-[2rem] shadow-2xl z-50 overflow-hidden"
          >
            <AnimatePresence mode="wait">

              {/* ── Main Menu View ── */}
              {view === 'menu' && (
                <motion.div
                  key="menu"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  /* Cap height so the footer always clears the fixed bottom nav (~5rem) plus the header offset */
                  className="flex flex-col max-h-[calc(100vh-12rem)]"
                >
                  {/* Header — Google-style: email + close, then centered avatar & greeting */}
                  <div className="px-4 pt-3 pb-4 border-b border-hairline bg-surface shrink-0">
                    {/* Close button */}
                    <div className="flex justify-end mb-1">
                      <button
                        onClick={() => { setIsOpen(false); setView('menu'); }}
                        className="text-ink-2 hover:text-ink transition-colors"
                        aria-label="Close menu"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Centered avatar + greeting */}
                    <div className="flex flex-col items-center text-center">
                      <div className="relative mb-2">
                        <div className="w-16 h-16 rounded-full bg-raised border-2 border-ink-mute flex items-center justify-center overflow-hidden">
                          {avatarPhoto ? (
                            <img referrerPolicy="no-referrer" src={avatarPhoto} alt="User" className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-7 h-7 text-ink-2" />
                          )}
                        </div>
                      </div>
                      {isElite && (
                        <span className="text-[9px] font-semibold uppercase tracking-widest text-accent-ink -mt-1 mb-1">
                          Elite
                        </span>
                      )}
                      {/* Season Pass holders see when it ends — that visible date is
                          what makes this a pass rather than a giveaway. Founding
                          comps have no expiry and correctly render nothing here. */}
                      {isElite && seasonPassEnds && (
                        <span className="text-[9px] font-semibold uppercase tracking-widest text-gold-ink -mt-1 mb-1">
                          Season Pass · through {seasonPassEnds}
                        </span>
                      )}
                      <div className="flex items-center gap-1.5">
                        <p className={cn("font-black text-base truncate", isElite ? "text-gold-ink" : "text-ink")}>
                          Hi, {(user?.displayName || 'Coach').split(' ')[0]}!
                        </p>
                      </div>
                      <div className="flex items-center justify-center space-x-1 mt-1">
                        <span className={cn("text-[9px] font-black uppercase tracking-widest", isHeadCoach ? "text-gold-ink" : "text-ink")}>Dugout Coach Since {coachSinceYear}</span>
                        {!isElite && (
                          <span className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest ml-1">· Free</span>
                        )}
                      </div>
                      <p className="text-[11px] font-bold text-ink-2 truncate max-w-full mt-1.5">{user?.email}</p>

                      <button
                        onClick={() => { setIsOpen(false); setIsShowingManageAccount(true); }}
                        className="mt-3 px-4 py-1.5 rounded-full border border-ink-mute text-ink-2 hover:bg-raised hover:border-ink-mute text-[11px] font-black uppercase tracking-widest transition-colors"
                      >
                        Manage Account
                      </button>
                    </div>
                  </div>

                  {/* Menu Actions — scrolls if the menu is taller than the viewport */}
                  <div className="p-2 flex-1 min-h-0 overflow-y-auto">
                    {/* Upgrade to Elite — top of menu for free users */}
                    {!isElite && (
                      <button
                        onClick={() => { setIsOpen(false); onUpgrade?.(); }}
                        className="w-full flex items-center justify-between p-4 hover:bg-canvas rounded-2xl transition-colors group text-left"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-lg bg-canvas border border-hairline flex items-center justify-center group-hover:bg-canvas transition-colors">
                            <Trophy className="w-4 h-4 text-accent-ink" />
                          </div>
                          <span className="text-xs font-black uppercase text-accent-ink">Upgrade to Elite</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-accent-ink" />
                      </button>
                    )}

                    {/* Team switcher — only when coach has multiple teams */}
                    {teams.length > 1 && (
                      <div className="px-2 pt-2 pb-3 mb-1 border-b border-hairline">
                        <div className="text-[9px] font-semibold uppercase text-ink-2 tracking-widest mb-2 px-2">Your Teams</div>
                        {[...teams]
                          .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
                          .map(t => (
                            <button
                              key={t.id}
                              onClick={() => { onSelectTeam?.(t.id); setIsOpen(false); setView('menu'); }}
                              className={cn(
                                "w-full text-left px-3 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-colors mb-1",
                                t.id === selectedTeamId
                                  ? "bg-canvas text-accent-ink"
                                  : "text-ink-2 hover:text-ink hover:bg-raised"
                              )}
                            >
                              {t.name}
                              <span className="block text-[9px] font-bold text-ink-2 normal-case tracking-normal mt-0.5">
                                {t.ageGroup} · {t.season} {t.seasonYear}
                              </span>
                            </button>
                          ))}
                      </div>
                    )}

                    {/* Add Team */}
                    <button
                      onClick={() => { onAddTeam?.(); setIsOpen(false); setView('menu'); }}
                      className="w-full flex items-center justify-between p-4 hover:bg-raised rounded-2xl transition-colors group text-left"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-lg bg-raised flex items-center justify-center group-hover:bg-hairline transition-colors">
                          <Plus className="w-4 h-4 text-ink-2 group-hover:text-ink" />
                        </div>
                        <span className="text-xs font-black uppercase text-ink-2 group-hover:text-ink transition-colors">Add Team</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-ink-2" />
                    </button>

                    {/* Add Assistant Coach — head coach + Elite only */}
                    {isHeadCoach && (
                      <button
                        onClick={() => isElite ? setView('invite') : onUpgrade?.()}
                        className="w-full flex items-center justify-between p-4 hover:bg-raised rounded-2xl transition-colors group text-left"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-lg bg-canvas border border-hairline flex items-center justify-center group-hover:bg-canvas transition-colors relative">
                            <UserPlus className="w-4 h-4 text-accent-ink" />
                            {!isElite && <Lock className="w-2 h-2 text-ink-2 absolute -bottom-0.5 -right-0.5" />}
                          </div>
                          <div>
                            <span className="text-xs font-black uppercase text-ink block">Add Assistant Coach</span>
                            <span className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest">{isElite ? 'Share your team code' : 'Elite feature'}</span>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-ink-2 group-hover:text-ink-2 transition-colors" />
                      </button>
                    )}

                    {/* Manage Subscription lives in the Manage Account panel now (removed here to avoid duplication) */}

                    <button
                      onClick={() => { setIsOpen(false); setIsShowingHelp(true); }}
                      className="w-full flex items-center justify-between p-4 hover:bg-raised rounded-2xl transition-colors group text-left"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-lg bg-raised flex items-center justify-center group-hover:bg-hairline transition-colors">
                          <HelpCircle className="w-4 h-4 text-ink-2 group-hover:text-ink" />
                        </div>
                        <span className="text-xs font-black uppercase text-ink-2 group-hover:text-ink transition-colors">Help &amp; FAQ</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-ink-2" />
                    </button>

                    <button
                      onClick={() => { setIsOpen(false); setIsShowingSupportForm(true); }}
                      className="w-full flex items-center justify-between p-4 hover:bg-raised rounded-2xl transition-colors group text-left"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-lg bg-raised flex items-center justify-center group-hover:bg-hairline transition-colors">
                          <MessageSquare className="w-4 h-4 text-ink-2 group-hover:text-ink" />
                        </div>
                        <span className="text-xs font-black uppercase text-ink-2 group-hover:text-ink transition-colors">Report a Bug</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-ink-2" />
                    </button>

                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-3 p-4 hover:bg-[#FBF0F0] rounded-2xl transition-colors group text-left"
                    >
                      <div className="w-8 h-8 rounded-lg bg-raised flex items-center justify-center group-hover:bg-[#FBF0F0] transition-colors">
                        <LogOut className="w-4 h-4 text-ink-2 group-hover:text-danger" />
                      </div>
                      <span className="text-xs font-black uppercase text-ink-2 group-hover:text-danger transition-colors">Sign Out</span>
                    </button>
                  </div>

                  {/* Footer — legal links, pinned below the scroll area */}
                  <div className="px-6 py-3 bg-surface text-center shrink-0 border-t border-hairline">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => setShowPrivacy(true)}
                        className="text-[10px] font-bold text-ink-2 hover:text-ink-2 transition-colors"
                      >
                        Privacy Policy
                      </button>
                      <span className="text-ink-2 text-[10px]">·</span>
                      <button
                        onClick={() => setShowTerms(true)}
                        className="text-[10px] font-bold text-ink-2 hover:text-ink-2 transition-colors"
                      >
                        Terms of Service
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ── Invite View ── */}
              {view === 'invite' && (
                <motion.div
                  key="invite"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                >
                  {/* Invite Header */}
                  <div className="p-5 border-b border-hairline bg-surface flex items-center space-x-3">
                    <button
                      onClick={() => setView('menu')}
                      className="w-7 h-7 rounded-lg bg-raised flex items-center justify-center hover:bg-hairline transition-colors shrink-0"
                    >
                      <ArrowLeft className="w-3.5 h-3.5 text-ink-2" />
                    </button>
                    <div>
                      <p className="font-black uppercase text-sm">Add Assistant Coach</p>
                      {team?.name && (
                        <p className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest truncate">{team.name}</p>
                      )}
                    </div>
                  </div>

                  <div className="p-5 space-y-4">

                    {/* Role cards */}
                    <div className="space-y-2">
                      {/* Head Coach role */}
                      <div className="p-3 rounded-xl bg-raised border border-ink-mute">
                        <div className="flex items-center space-x-2 mb-1">
                          <Crown className="w-3 h-3 text-gold-ink" />
                          <span className="text-[10px] font-semibold text-gold-ink uppercase tracking-widest">Head Coach</span>
                          <span className="ml-auto text-[8px] font-semibold text-ink-2 uppercase tracking-widest">You</span>
                        </div>
                        <p className="text-[10px] text-ink-2 leading-relaxed">
                          Full read/write access. All player OVRs and xOVR calculations use your scouting ratings.
                        </p>
                      </div>

                      {/* Assistant Coach role */}
                      <div className="p-3 rounded-xl bg-raised border border-ink-mute">
                        <div className="flex items-center space-x-2 mb-1">
                          <User className="w-3 h-3 text-ink" />
                          <span className="text-[10px] font-semibold text-ink uppercase tracking-widest">Assistant Coach</span>
                        </div>
                        <p className="text-[10px] text-ink-2 leading-relaxed">
                          Can write their own scouting report on each player. Their OVR ratings don't affect xOVR unless you enable it in Settings. Can run live games (track pitches on your behalf) and view all team stats.
                        </p>
                      </div>
                    </div>

                    {/* Team join code */}
                    {team?.joinCode && (
                      <div className="space-y-2">
                        <label className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest block">
                          Send this code to your coaches
                        </label>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-surface border border-ink-mute rounded-xl px-3 py-2.5 font-black text-lg text-center uppercase tracking-widest text-ink select-all">
                            {team.joinCode}
                          </div>
                          <button
                            onClick={() => {
                              const msg = `I've set up ${team.name} on Dugout and would like your input on our players. Use ${team.joinCode} to join our team at dugoutbaseball.app`;
                              window.location.href = `sms:?&body=${encodeURIComponent(msg)}`;
                            }}
                            className="px-3 py-2.5 bg-ink hover:bg-ink text-surface rounded-xl font-black text-xs uppercase tracking-widest transition-colors flex items-center gap-1.5"
                          >
                            <MessageSquare className="w-3.5 h-3.5" />
                            Send
                          </button>
                        </div>
                        <p className="text-[9px] text-ink-2 text-center leading-relaxed pt-1">
                          Opens iMessage or SMS with a pre-filled invite.
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
      {isShowingHelp && (
        <HelpCenter
          onClose={() => setIsShowingHelp(false)}
          onContactSupport={() => { setIsShowingHelp(false); setIsShowingSupportForm(true); }}
        />
      )}
      {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
      {isShowingManageAccount && (
        <ManageAccountModal
          user={user}
          coachData={coachData}
          teams={teams}
          isElite={isElite}
          eliteSources={eliteSources}
          grantExpiresAt={grantExpiresAt}
          onClose={() => setIsShowingManageAccount(false)}
          onManageBilling={handleManageSubscription}
          onUpgrade={onUpgrade}
        />
      )}
      {isShowingSupportForm && (
        <SupportForm
          teamId={team?.id ?? ''}
          teamName={team?.name ?? ''}
          userEmail={user?.email ?? ''}
          uid={user?.uid ?? ''}
          onClose={() => setIsShowingSupportForm(false)}
        />
      )}
    </div>
  );
};
